/* eslint-disable no-unused-vars */
// Assistant Housing Validator
// Handles validation for O-Ring Assistant using the ORingAssistant/CheckHousing API
import {ref, watch, readonly} from 'vue'
import {storeToRefs} from 'pinia'
import {useORingCalculatorStore} from '~/store/oring-calculator'
import {useOringCalculatorService} from '~/composables/oring_calc_service'
import type {FrontendError} from '~/types/oring-api-responses'
import type * as OringApiRequests from '@/types/oring-api'
import useI18nExtended from '~/composables/usei18nExtended'

export function useAssistantHousingValidator() {
  const store = useORingCalculatorStore()
  const {oringCalculatorImperial} = storeToRefs(store)
  const i18n = useI18nExtended()

  // Local ref to store validation errors by field key
  const validationErrors = ref<Record<string, string>>({})

  // Build the request for assistant housing validation
  function buildAssistantCheckHousingRequest(
    toleranceData: Record<string, string | null>,
    parameterInputData: Record<string, number | null>
  ): OringApiRequests.ORingAssistantCheckHousingRequest {
    // Defensive checks to ensure required IDs exist
    if (!store.oringAssistantApplicationId || !store.oringAssistantGeometryId) {
      // eslint-disable-next-line no-console
      console.error('Missing required application or geometry ID for housing validation')
      throw new Error('Cannot validate housing without application and geometry ID')
    }

    // Get base housing values from store
    const baseHousingValues: OringApiRequests.ORingSearchParameter[] = [
      {searchParameterKey: 'applicationID', searchValue: store.oringAssistantApplicationId},
      {searchParameterKey: 'geometryID', searchValue: store.oringAssistantGeometryId},
      {searchParameterKey: 'onShaft', searchValue: store.oringAssistantOnShaft ? 1 : 0},
      {searchParameterKey: 'chamfer', searchValue: 15}
    ]

    // Get search parameters from store (uses housing object values)
    const searchParameters = store.oringAssistantSearchParameters()

    return {
      language: i18n.locale.value,
      imperial: store.oringCalculatorImperial,
      housingValues: [
        ...baseHousingValues,
        ...searchParameters
      ]
    }
  }

  // Call the assistant check housing API
  async function callCheckHousingAPI(
    toleranceData: Record<string, string | null>,
    parameterInputData: Record<string, number | null>,
    emitValidationErrors: (errors: FrontendError[]) => void,
    emitHousingValidated: () => void,
    emitApiError: (error: any) => void
  ): Promise<void> {
    try {
      const oringService = useOringCalculatorService()
      const request = buildAssistantCheckHousingRequest(toleranceData, parameterInputData)

      const result = await oringService.checkHousingORingAssistant(request)

      // Handle the result
      if (result.errorDTO?.error && result.errorDTO.errors?.length > 0) {
        // Handle housing validation errors and map them to fields
        mapValidationErrorsToFields(result.errorDTO.errors)
        emitValidationErrors(result.errorDTO.errors)
      } else {
        // Clear any existing errors when validation passes
        validationErrors.value = {}
        emitHousingValidated()
      }

    } catch (apiError) {
      // Handle API error - this could be the buildRequest error or the API call error
      // eslint-disable-next-line no-console
      console.error('Assistant housing validation error:', apiError)
      emitApiError(apiError)
    }
  }

  // Store the latest validation arguments for re-validation on unit change
  let lastToleranceData: Record<string, string | null> | null = null
  let lastParameterInputData: Record<string, number | null> | null = null
  let lastEmitValidationErrors: ((errors: FrontendError[]) => void) | null = null
  let lastEmitHousingValidated: (() => void) | null = null
  let lastEmitApiError: ((error: any) => void) | null = null

  // Main validation function
  function checkHousing(
    toleranceData: Record<string, string | null>,
    parameterInputData: Record<string, number | null>,
    emitValidationErrors: (errors: FrontendError[]) => void,
    emitHousingValidated: () => void,
    emitApiError: (error: any) => void
  ) {
    // Store the latest arguments for re-validation on unit change
    lastToleranceData = toleranceData
    lastParameterInputData = parameterInputData
    lastEmitValidationErrors = emitValidationErrors
    lastEmitHousingValidated = emitHousingValidated
    lastEmitApiError = emitApiError

    callCheckHousingAPI(toleranceData, parameterInputData, emitValidationErrors, emitHousingValidated, emitApiError)
  }

  // Extract field key from validation error
  function extractFieldKeyFromError(error: FrontendError): string | null {
    // Try to extract field key from elementName first
    if (error.elementName) {
      const elementName = error.elementName.toLowerCase()

      // Direct mapping from element names to field keys used in assistant
      const elementToFieldMap: Record<string, string> = {
        'd3': 'd3',
        'd4': 'd4',
        'd5': 'd5',
        'd6': 'd6',
        'd8': 'd8',
        'd9': 'd9',
        'd10': 'd10',
        'b1': 'b1',
        'b1in': 'b1in',
        'b1out': 'b1out',
        'b4': 'b4',
        'h': 'height',
        'height': 'height'
      }

      if (elementToFieldMap[elementName]) {
        return elementToFieldMap[elementName]
      }
    }

    return null
  }

  // Map validation errors to field keys
  function mapValidationErrorsToFields(errors: FrontendError[]) {
    // Clear existing errors
    validationErrors.value = {}

    errors.forEach(error => {
      const fieldKey = extractFieldKeyFromError(error)
      if (fieldKey && error.errorMessage) {
        let errorMessage = error.errorMessage

        // Only append threshold value with units if both thresholdValue and units are available
        // eslint-disable-next-line eqeqeq
        if (error.thresholdValue != null) {
          const isImperial = oringCalculatorImperial.value
          const unit = isImperial
            ? i18n.tv('inch', 'unitsOfMeasure')
            : i18n.tv('mm', 'unitsOfMeasure')
          // Round threshold value appropriately
          const decimals = isImperial ? 4 : 3
          const roundedValue = Number(error.thresholdValue).toFixed(decimals)
          errorMessage += ` ${roundedValue} ${unit}`
        }

        validationErrors.value[fieldKey] = errorMessage
      }
    })
  }

  // Get error message for a specific field
  function getErrorForField(key: string): string {
    return validationErrors.value[key] || ''
  }

  // Validate function (always returns true for assistant, validation is async)
  function validate(): boolean {
    return true
  }

  return {
    validationErrors: readonly(validationErrors),
    checkHousing,
    getErrorForField,
    validate
  }
}
